/**
 * 인증 스토어.
 *
 * 토큰과 사용자는 localStorage 에 유지된다(새로고침해도 로그인 유지).
 * 부팅 시 `init()` 이 토큰을 검증하고, 만료됐으면 조용히 로그아웃한다.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import api, { bindAuth, syncToken, errorMessage } from '../api';

const useAuthStore = create(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      /** idle → booting → authed | guest */
      status: 'idle',
      loading: false,
      error: null,

      /** 앱 부팅 시 한 번. 저장된 토큰을 검증한다. */
      init: async () => {
        bindAuth({
          getToken: () => get().token,
          unauthorized: () => get().forceLogout()
        });
        const { token } = get();
        if (!token) {
          set({ status: 'guest' });
          return;
        }
        set({ status: 'booting' });
        try {
          const user = await api.auth.me();
          set({ user, status: 'authed' });
        } catch (e) {
          get().forceLogout();
        }
      },

      login: async (email, password) => {
        set({ loading: true, error: null });
        try {
          const { token, user } = await api.auth.login({ email, password });
          syncToken(token);
          set({ token, user, status: 'authed', loading: false });
          return true;
        } catch (e) {
          set({ error: errorMessage(e), loading: false });
          return false;
        }
      },

      signup: async payload => {
        set({ loading: true, error: null });
        try {
          const { token, user } = await api.auth.signup(payload);
          syncToken(token);
          set({ token, user, status: 'authed', loading: false });
          return true;
        } catch (e) {
          set({ error: errorMessage(e), loading: false });
          return false;
        }
      },

      logout: async () => {
        try {
          await api.auth.logout();
        } catch (e) {
          /* 서버 로그아웃 실패는 무시 — 로컬 세션은 반드시 지운다 */
        }
        get().forceLogout();
      },

      forceLogout: () => {
        syncToken(null);
        set({ token: null, user: null, status: 'guest', error: null });
      },

      refreshMe: async () => {
        if (!get().token) return null;
        try {
          const user = await api.auth.me();
          set({ user });
          return user;
        } catch (e) {
          return null;
        }
      },

      updateProfile: async patch => {
        set({ loading: true, error: null });
        try {
          const user = await api.auth.updateProfile(patch);
          set({ user, loading: false });
          return { ok: true, user };
        } catch (e) {
          const message = errorMessage(e);
          set({ error: message, loading: false });
          return { ok: false, message };
        }
      },

      changePassword: async payload => {
        set({ loading: true, error: null });
        try {
          await api.auth.changePassword(payload);
          set({ loading: false });
          return { ok: true };
        } catch (e) {
          const message = errorMessage(e);
          set({ error: message, loading: false });
          return { ok: false, message };
        }
      },

      clearError: () => set({ error: null })
    }),
    {
      name: 'trinity.auth.v3',
      storage: createJSONStorage(() => window.localStorage),
      partialize: s => ({ token: s.token, user: s.user })
    }
  )
);

/** 셀렉터 — 리렌더 최소화 */
export const selectIsLoggedIn = s => !!s.token && !!s.user;
export const selectUser = s => s.user;

export default useAuthStore;
